import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Params } from '@angular/router';
import { QarepaApiLinks } from '@qarepa/common';
import { OAuthStorageService } from '@qaroni-app/auth/services/o-auth-storage.service';
import { Invitation } from '@qaroni-app/invitations/types/invitation';
import { InvitationApiResponse } from '@qaroni-app/invitations/types/invitation-response';
import { InvitationStatusEnum } from '@qaroni-app/invitations/types/invitation-status.enum';
import { CommonsHttpService } from '@qaroni-core/services/commons/commons-http/commons-http.service';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import {
  CreateEmployeeDTO,
  Employee,
  EmployeeNames,
  UpdateEmployeeDTO,
} from '../types/employee';
import { EmployeeAddress } from '../types/employee-address';
import { EmployeeCache } from '../types/employee-cache.class';
import { EmployeeNamesApiResponse } from '../types/employee-names-response';
import { EmployeeReport } from '../types/employee-report';
import { EmployeeReportApiResponse } from '../types/employee-report-response';
import { EmployeeApiResponse } from '../types/employee-response';
import { InvitationCache } from '../types/invitation-cache';
import { EmployeeHttpService } from './employee-http.service';
import { EmployeeSnackbarsService } from './employee-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private commonsHttp = inject(CommonsHttpService);
  private http = inject(EmployeeHttpService);
  private oAuthStorage = inject(OAuthStorageService);
  readonly snackbars = inject(EmployeeSnackbarsService);

  protected readonly employeeSubject = new Subject<Employee>();
  protected readonly employeeNamesSubject = new BehaviorSubject<
    EmployeeNames[]
  >([]);

  private employee: EmployeeCache = new EmployeeCache();
  private invitation: InvitationCache = new InvitationCache();

  protected readonly invitationSubject = new Subject<Invitation>();
  // TODO: Think and study when remove method will call

  protected readonly employeesSubject = new ReplaySubject<Employee[]>();
  protected readonly paginationLinksSubject = new Subject<QarepaApiLinks>();
  protected readonly messageSubject = new Subject<string>();
  protected readonly reportSubject = new Subject<EmployeeReport>();

  // TODO: Check if can't use and remove this shit of message
  public getMessage$() {
    return this.messageSubject.asObservable().pipe(shareReplay(1));
  }

  // ==========================================================================================
  // Get Employees
  // ==========================================================================================

  public getEmployees$() {
    return this.employeesSubject.asObservable().pipe(shareReplay(1));
  }

  public resetEmployees(): void {
    return this.employeesSubject.next(null);
  }

  public getEmployees(companyID: number, params?: Params): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.getEmployees$(companyID, params).subscribe({
      next: this.nextGetEmployees,
      error: this.errorGetEmployees,
    });
  }

  private nextGetEmployees = (data: EmployeeApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const employees: Employee[] = data.body.result;
      this.employeesSubject.next(employees);
      const paginationLinks: QarepaApiLinks = data.body.links;
      this.paginationLinksSubject.next(paginationLinks);
    } else {
      this.employeesSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorGetEmployees = (error: HttpErrorResponse): void => {
    this.employeesSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Register Employee
  // ==========================================================================================

  public getEmployee$() {
    return this.employeeSubject.asObservable().pipe(shareReplay(1));
  }

  public resetEmployeeCache() {
    return this.employee.empty();
  }

  public registerEmployee(
    companyID: number,
    createEmployeeDTO: CreateEmployeeDTO
  ): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.registerEmployee$(companyID, createEmployeeDTO).subscribe({
      next: this.nextRegisterEmployee,
      error: this.errorRegisterEmployee,
    });
  }

  private nextRegisterEmployee = (data: EmployeeApiResponse): void => {
    if (
      this.commonsHttp.validationsHttp.verifyStatus201(data) &&
      data?.body?.result?.length
    ) {
      this.employee.set(data.body.result[0]);
      this.employeeSubject.next(this.employee.get());
    } else {
      this.employeeSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorRegisterEmployee = (error: HttpErrorResponse): void => {
    this.employeeSubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0044')) {
      this.messageSubject.next('Failure Quota Of Users Is Exhausted');
      // this.snackbars.failureQuotaOfUsersIsExhausted();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Get Employee
  // ==========================================================================================

  public getInvitation$() {
    return this.invitationSubject.asObservable().pipe(shareReplay(1));
  }

  public getEmployee(companyID: number, employeeID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    if (this.employee.canReturn(companyID, employeeID)) {
      this.employeeSubject.next(this.employee.get());
      this.invitationSubject.next(this.invitation.get());
    } else if (this.employee.canApi(companyID, employeeID)) {
      this.employee.startToFly();
      this.http
        .getEmployee$(companyID, employeeID)
        .pipe(finalize(() => this.employee.endFlying()))
        .subscribe({
          next: this.nextGetEmployee,
          error: this.errorGetEmployee,
        });
    }
  }

  private nextGetEmployee = (data: EmployeeApiResponse): void => {
    if (
      this.commonsHttp.validationsHttp.verifyStatus200(data) &&
      data?.body?.result?.length
    ) {
      this.employee.set(data.body.result[0]);
      this.employeeSubject.next(this.employee.get());

      if (!data?.body?.included) {
        this.invitation.empty();
      }

      const invitation: Invitation = data.body
        .included as unknown as Invitation;
      this.invitation.set(invitation);
      this.invitationSubject.next(this.invitation.get());
    } else {
      this.employeeSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorGetEmployee = (error: HttpErrorResponse): void => {
    this.employeeSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Update Employee
  // ==========================================================================================

  public updateEmployee(
    companyID: number,
    employeeID: number,
    updateEmployeeDTO: UpdateEmployeeDTO
  ): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http
      .updateEmployee$(companyID, employeeID, updateEmployeeDTO)
      .subscribe({
        next: this.nextUpdateEmployee,
        error: this.errorUpdateEmployee,
      });
  }

  private nextUpdateEmployee = (data: EmployeeApiResponse): void => {
    if (
      this.commonsHttp.validationsHttp.verifyStatus200(data) &&
      data?.body?.result?.length
    ) {
      this.employee.set(data.body.result[0]);
      this.employeeSubject.next(this.employee.get());
      this.messageSubject.next('OK updated employee');
      this.snackbars.successUpdated();
    } else {
      this.employeeSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorUpdateEmployee = (error: HttpErrorResponse): void => {
    this.employeeSubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0044')) {
      this.messageSubject.next('Failure Quota Of Users Is Exhausted');
      // this.snackbars.failureQuotaOfUsersIsExhausted();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Update Employee Address
  // ==========================================================================================

  public updateEmployeeAddress(
    companyID: number,
    employeeID: number,
    address: EmployeeAddress
  ): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.updateEmployeeAddress$(companyID, employeeID, address).subscribe({
      next: this.nextUpdateEmployeeAddress,
      error: this.errorUpdateEmployeeAddress,
    });
  }

  private nextUpdateEmployeeAddress = (data: EmployeeApiResponse): void => {
    if (
      this.commonsHttp.validationsHttp.verifyStatus200(data) &&
      data?.body?.result?.length
    ) {
      this.employee.set(data.body.result[0]);
      this.employeeSubject.next(this.employee.get());
    } else {
      this.employeeSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorUpdateEmployeeAddress = (error: HttpErrorResponse): void => {
    this.employeeSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Remove Employee
  // ==========================================================================================

  public removeEmployee(companyID: number, employeeID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.removeEmployee$(companyID, employeeID).subscribe({
      next: this.nextRemoveEmployee,
      error: this.errorRemoveEmployee,
    });
  }

  private nextRemoveEmployee = (data: EmployeeApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus204(data)) {
      this.employee.empty();
      this.messageSubject.next('OK deleted employee');
    } else {
      this.messageSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorRemoveEmployee = (error: HttpErrorResponse): void => {
    this.messageSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Send Invitation
  // ==========================================================================================

  public sendInvitation(companyID: number, invitationID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.sendInvitation$(companyID, invitationID).subscribe({
      next: this.nextSendInvitation,
      error: this.errorSendInvitation,
    });
  }

  private nextSendInvitation = (data: InvitationApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const invitation: Invitation = data.body.result[0];
      if (invitation?.status === InvitationStatusEnum.PENDING) {
        this.snackbars.successSendInvitation();
      }
      this.invitationSubject.next(invitation);
    } else {
      this.invitationSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorSendInvitation = (error: HttpErrorResponse): void => {
    this.invitationSubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0044')) {
      this.messageSubject.next('Failure Quota Of Users Is Exhausted');
      // this.snackbars.failureQuotaOfUsersIsExhausted();
    } else {
      this.snackbars.failureSendInvitation();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Resend Invitation
  // ==========================================================================================

  public resendInvitation(companyID: number, invitationID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.resendInvitation$(companyID, invitationID).subscribe({
      next: this.nextResendInvitation,
      error: this.errorResendInvitation,
    });
  }

  private nextResendInvitation = (data: InvitationApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const invitation: Invitation = data.body.result[0];
      if (invitation?.status === InvitationStatusEnum.PENDING) {
        this.snackbars.successResendInvitation();
      }
      this.invitationSubject.next(invitation);
    } else {
      this.invitationSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorResendInvitation = (error: HttpErrorResponse): void => {
    this.invitationSubject.next(null);
    if (this.commonsHttp.errorsHttp.isControlledError(error, 'E0044')) {
      this.messageSubject.next('Failure Quota Of Users Is Exhausted');
    } else {
      this.snackbars.failureSendInvitation();
    }
    this.commonsHttp.errorsHttp.communication(error);
  };

  public getEmployeesNames$() {
    return this.employeeNamesSubject.asObservable().pipe(shareReplay(1));
  }

  public employeesNames(companyID: number): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.getNames$(companyID).subscribe({
      next: this.nextGetEmployeesNames,
      error: this.errorGetEmployeesNames,
    });
  }

  private nextGetEmployeesNames = (data: EmployeeNamesApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const employeesNames: Partial<EmployeeNames>[] = data.body.result;
      this.employeeNamesSubject.next(employeesNames);
    } else {
      this.employeeNamesSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorGetEmployeesNames = (error: HttpErrorResponse): void => {
    this.employeeNamesSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
  };

  // ==========================================================================================
  // Pagination Links
  // ==========================================================================================

  public getPaginationLinks$() {
    return this.paginationLinksSubject.asObservable().pipe(shareReplay(1));
  }

  public employeeReport$() {
    return this.reportSubject.asObservable().pipe(shareReplay(1));
  }

  public exports(companyID: number, params?: Params): void {
    if (!this.oAuthStorage.hasOAuth) {
      return;
    }

    this.http.exports$(companyID, params).subscribe({
      next: this.nextExports,
      error: this.errorExports,
    });
  }

  private nextExports = (data: EmployeeReportApiResponse): void => {
    if (this.commonsHttp.validationsHttp.verifyStatus200(data)) {
      const report = data.body.result[0];
      this.reportSubject.next(report);
    } else {
      this.reportSubject.next(null);
      this.commonsHttp.errorsHttp.apiInvalidResponse(data);
    }
  };

  private errorExports = (error: HttpErrorResponse): void => {
    this.reportSubject.next(null);
    this.commonsHttp.errorsHttp.communication(error);
    this.snackbars.failExport();
  };
}
